import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtT, fmtN, downloadFile, ESTADO_STYLE, FIRMA_STYLE } from "./lib";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Loader2, Search, Download, ChevronDown, FileText, Sheet, Image as ImageIcon, Filter, Lock, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";

export default function GranosContratos() {
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ cultivos: [], tipos: [], estados: [], firmados: [], vendedores: [], zafras: [] });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [f, setF] = useState({ zafra: "__all__", cultivo: "__all__", vendedor: "__all__", tipo: "__all__", firmado: "__all__", estado: "__all__" });
  const [productor, setProductor] = useState("");
  const [incluirFiniquitados, setIncluirFiniquitados] = useState(false);

  const buildParams = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== "__all__") params[k] = v; });
    if (productor.trim()) params.productor = productor.trim();
    if (incluirFiniquitados) params.incluir_finiquitados = 1;
    return params;
  }, [f, productor, incluirFiniquitados]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/granos/contratos", { params: buildParams() });
      setRows(res.data.contratos || []);
      setFacets({
        cultivos: res.data.cultivos || [], tipos: res.data.tipos || [], estados: res.data.estados || [],
        firmados: res.data.firmados || [], vendedores: res.data.vendedores || [], zafras: res.data.zafras || [],
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar contratos");
    } finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setFF = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const activeFilters = Object.values(f).filter((v) => v !== "__all__").length + (productor.trim() ? 1 : 0);

  const exportAs = async (kind) => {
    const map = {
      excel: ["/granos/export", "Tecnomyl_Granos.xlsx"],
      pdf: ["/granos/export-pdf", "Tecnomyl_Granos.pdf"],
      image: ["/granos/export-image", "Tecnomyl_Granos.png"],
    };
    const [path, name] = map[kind];
    try {
      toast.loading("Generando archivo...", { id: "gexp" });
      await downloadFile(path, name, buildParams());
      toast.success("Descarga lista", { id: "gexp" });
    } catch {
      toast.error("No se pudo exportar", { id: "gexp" });
    }
  };

  const FSel = ({ k, label, opts }) => (
    <div>
      <label className="text-[11px] font-semibold text-slate-500">{label}</label>
      <Select value={f[k]} onValueChange={setFF(k)}>
        <SelectTrigger className="h-10 mt-1" data-testid={`granos-filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Todos</SelectItem>
          {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-4 space-y-3" data-testid="granos-contratos-tab">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={productor} onChange={(e) => setProductor(e.target.value)} placeholder="Buscar productor / cliente..."
          className="h-11 w-full rounded-xl border border-slate-200 pl-9 pr-3 text-sm" data-testid="granos-search-productor" />
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="granos-toggle-filters"
          className="gap-2 h-10 flex-1">
          <Filter className="h-4 w-4" /> Filtros {activeFilters > 0 && <span className="ml-1 rounded-full bg-[#0f5233] text-white text-[10px] px-1.5">{activeFilters}</span>}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" disabled={!rows.length} data-testid="granos-export-menu" className="gap-2 h-10 flex-1 bg-[#0f5233] hover:bg-[#0b3f27]">
              <Download className="h-4 w-4" /> Exportar <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportAs("pdf")} data-testid="granos-export-pdf"><FileText className="h-4 w-4 mr-2 text-red-600" /> PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportAs("excel")} data-testid="granos-export-excel"><Sheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportAs("image")} data-testid="granos-export-image"><ImageIcon className="h-4 w-4 mr-2 text-sky-600" /> Imagen HD</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <button onClick={() => setIncluirFiniquitados((v) => !v)} data-testid="granos-toggle-finiquitados"
        className={`flex items-center gap-2 w-full justify-center rounded-xl border px-3 h-10 text-sm font-medium transition-colors ${
          incluirFiniquitados ? "bg-[#0f5233] text-white border-[#0f5233]" : "bg-white text-slate-600 border-slate-200"
        }`}>
        {incluirFiniquitados ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        {incluirFiniquitados ? "Mostrando finiquitados" : "Incluir finiquitados"}
      </button>

      {showFilters && (
        <div className="grid grid-cols-2 gap-2 rounded-2xl border bg-white p-3" data-testid="granos-filters-panel">
          <FSel k="zafra" label="Zafra" opts={facets.zafras} />
          <FSel k="cultivo" label="Cultivo" opts={facets.cultivos} />
          <FSel k="vendedor" label="Vendedor" opts={facets.vendedores} />
          <FSel k="tipo" label="Tipo" opts={facets.tipos} />
          <FSel k="firmado" label="Firma" opts={facets.firmados} />
          <FSel k="estado" label="Estado" opts={facets.estados} />
        </div>
      )}

      <p className="text-xs text-slate-400" data-testid="granos-count">{rows.length} contrato(s)</p>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0f5233]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-400">Sin contratos. Importá las planillas.</div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((c) => {
            const id = `${c.cultivo}-${c.contrato}`;
            const op = expanded === id;
            return (
              <div key={id} className="rounded-2xl border bg-white overflow-hidden" data-testid={`granos-card-${c.contrato}`}>
                <button onClick={() => setExpanded(op ? null : id)} className="w-full text-left px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{c.productor}</p>
                      <p className="text-[11px] text-slate-400">Ctr {c.contrato} · {c.cultivo} · {c.tipo_contrato}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${ESTADO_STYLE[c.estado] || ""}`}>{c.estado}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-4 gap-1 text-[11px]">
                    <MiniStat label="Contrat." value={fmtT(c.contratada)} />
                    <MiniStat label="Pend." value={fmtT(c.pendiente)} strong />
                    <MiniStat label="% Cump." value={`${fmtN(c.pct_cumplido)}%`} />
                    <MiniStat label="Firma" value={c.firmado} cls={FIRMA_STYLE[c.firmado]} />
                  </div>
                </button>

                {op && (
                  <div className="border-t bg-slate-50 px-4 py-3 space-y-2" data-testid={`granos-card-detail-${c.contrato}`}>
                    {(c.precio_cerrado != null || c.costo_cerrado != null || c.lugar_entrega) && (
                      <div className="flex flex-wrap gap-2 text-[11px]">
                        {c.precio_cerrado != null && (
                          <span className="rounded-md bg-emerald-100 text-emerald-800 px-2 py-1 font-medium flex items-center gap-1">
                            <Lock className="h-3 w-3" /> Precio {fmtN(c.precio_cerrado)} · {fmtT(c.precio_cerrado_tn)} t
                          </span>
                        )}
                        {c.costo_cerrado != null && (
                          <span className="rounded-md bg-teal-100 text-teal-800 px-2 py-1 font-medium flex items-center gap-1">
                            <Lock className="h-3 w-3" /> Costo {fmtN(c.costo_cerrado)} · {fmtT(c.costo_cerrado_tn)} t
                          </span>
                        )}
                        {c.lugar_entrega && <span className="rounded-md bg-amber-100 text-amber-800 px-2 py-1 font-medium">📍 {c.lugar_entrega}</span>}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-500">
                      Condición: <b>{c.condicion || "—"}</b> · Precio {fmtN(c.precio)} · Costo {fmtN(c.costo)} · S/CyP {fmtT(c.sin_costo_precio)} t
                    </div>
                    {(c.depositos || []).length > 0 && (
                      <div className="overflow-x-auto rounded-lg border bg-white">
                        <table className="w-full text-[11px]">
                          <thead className="text-slate-400"><tr>
                            {["Depósito", "Contr.", "Entr.", "Pend.", "Precio", "Costo"].map((h, i) => (
                              <th key={i} className={`px-2 py-1 ${i ? "text-right" : "text-left"}`}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {c.depositos.map((d, i) => (
                              <tr key={i} className="border-t">
                                <td className="px-2 py-1">{d.deposito || "—"}</td>
                                <td className="px-2 py-1 text-right">{fmtT(d.contratada)}</td>
                                <td className="px-2 py-1 text-right">{fmtT(d.entregada)}</td>
                                <td className="px-2 py-1 text-right">{fmtT(d.pendiente)}</td>
                                <td className="px-2 py-1 text-right text-emerald-700">{d.prom_precio ? fmtN(d.prom_precio) : "—"}</td>
                                <td className="px-2 py-1 text-right text-slate-500">{d.prom_costo ? fmtN(d.prom_costo) : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const MiniStat = ({ label, value, strong, cls }) => (
  <div className="rounded-lg bg-slate-50 px-2 py-1.5">
    <p className="text-slate-400">{label}</p>
    <p className={`${strong ? "font-bold text-slate-900" : `font-medium ${cls || "text-slate-700"}`}`}>{value}</p>
  </div>
);
